// ─────────────────────────────────────────────
//  useStaff — live, normalized view of the `staff` collection (the source of
//  truth for the assignable people pool). Returns the doctor / provider pools
//  in the board-pool shape, so operational pages consume staff the same way
//  they used to consume users.
//
//  The staff pool is shared facility-wide by design (see firestore.listenToStaff
//  comment), so this listens to the whole collection. Facility scoping, if ever
//  needed, can be layered with a `facilityId` argument later.
// ─────────────────────────────────────────────
import { useEffect, useMemo, useState } from "react";
import { listenToStaff } from "../firebase/firestore";
import { onAuth } from "../firebase/auth";
import { splitStaffPool, sortPoolByPrimaryFacility } from "../domain/staff";
import { useAuth } from "../context/AuthContext";

export function useStaff() {
  const [raw, setRaw] = useState([]);
  const [loading, setLoading] = useState(true);

  // Subscribe only while signed in, and RE-subscribe on every sign-in. The old
  // unconditional subscription ran once at app-shell mount — on the /login page
  // that was pre-auth, Firestore denied it, the listener died, and the
  // AlertContext / MessagingContext staff pools stayed EMPTY until a manual
  // refresh (alert targeting by staff id silently failed after a cold login).
  useEffect(() => {
    let unsubStaff = null;
    const unsubAuth = onAuth((authUser) => {
      if (unsubStaff) { unsubStaff(); unsubStaff = null; }
      if (!authUser) {
        setRaw([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      unsubStaff = listenToStaff(
        (snap) => {
          setRaw(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
          setLoading(false);
        },
        (err) => {
          console.error("Staff listener failed:", err);
          setLoading(false);
        }
      );
    });
    return () => {
      unsubAuth();
      if (unsubStaff) unsubStaff();
    };
  }, []);

  // Re-split only when the snapshot changes — this hook has several consumers
  // that re-render often (board, alert + messaging providers). Pools are
  // ordered with the viewer's PRIMARY facility first (first-login question),
  // so every provider dropdown surfaces the people they work with most.
  const { user } = useAuth();
  const primaryId = user?.primaryFacilityId || null;
  const { all, doctors, providers } = useMemo(() => {
    const pools = splitStaffPool(raw);
    return {
      all: sortPoolByPrimaryFacility(pools.all, primaryId),
      doctors: sortPoolByPrimaryFacility(pools.doctors, primaryId),
      providers: sortPoolByPrimaryFacility(pools.providers, primaryId),
    };
  }, [raw, primaryId]);
  return { staff: all, doctors, providers, loading };
}
